import { PopupConfirm } from "@components/generals/popup";
import { SpinerPage } from "@components/loaders/spiner";
import { FinanceService } from "@services/finance.service";
import { useNotificationStore } from "@stores/notification.store";
import { useThemeStore } from "@stores/theme.store";
import { useMutation } from "@tanstack/react-query";
import { toRp } from "@utils/helper";
import copy from "copy-to-clipboard";
import moment from "moment";
import { useEffect, useState } from "react";
import { BiCheck, BiTime, BiX } from "react-icons/bi";
import { IoCopy } from "react-icons/io5";
import { useLocation } from "react-router-dom";

export const VirtualAccountReportDetailPage = () => {
  const { state } = useLocation();
  const { pushNotification } = useNotificationStore((state) => state);
  const { setFooter, setHeader, setAppBar } = useThemeStore((state) => state);
  const [status, setStatus] = useState<string>(state?.vaStatus);
  const [trxID, setTrxID] = useState<string>("");
  const [popupCancel, setPopupCancel] = useState<boolean>(false);

  const { mutate, isLoading } = useMutation(
    FinanceService.virtualAccountCancel,
    {
      onSuccess: (response) => {
        if (response.success) {
          setStatus("EXPIRED");
          setTrxID("");
          pushNotification({
            type: "success",
            message: "Virtual account berhasil dibatalkan",
          });
        } else {
          pushNotification({
            type: "error",
            message: response.message,
          });
        }
      },
      onError: (error: any) => {
        pushNotification({
          type: "error",
          message: error.message ?? "Internal Error",
        });
      },
    }
  );

  useEffect(() => {
    setFooter(false);
    setHeader({
      type: "small",
      title: "Detail Laporan VA",
      buttonBack: true,
      avatar: true,
    });
    setAppBar({ title: "Detail Laporan VA", leading: "back" });
  }, [setFooter, setHeader, setAppBar]);

  const onCopy = () => {
    copy(state?.vaNumber);
    pushNotification({
      type: "success",
      message: "Nomor VA berhasil disalin!",
    });
  };

  const onCopyNominal = () => {
    copy(state?.vaTrxAmount);
    pushNotification({
      type: "success",
      message: "Nominal berhasil disalin!",
    });
  };

  const onOpenPopupCancel = (id: string) => {
    setTrxID(id);
    setPopupCancel(true);
  };

  const onCancel = () => {
    setPopupCancel(false);
    mutate(trxID);
  };

  return (
    <>
      <SpinerPage show={isLoading} />
      <div className="tw-relative tw-z-1 text-center mb-3">
        {status === "PENDING" ? (
          <BiTime className="color-yellow-dark tw-text-5xl tw-bg-[#fff] tw-border-4 tw-border-[#435d288a] tw-border-solid tw-rounded-full" />
        ) : status === "EXPIRED" ? (
          <BiX className="color-red-dark tw-text-5xl tw-bg-[#fff] tw-border-4 tw-border-[#435d288a] tw-border-solid tw-rounded-full" />
        ) : (
          <BiCheck className="color-highlight tw-text-5xl tw-bg-[#fff] tw-border-4 tw-border-[#435d288a] tw-border-solid tw-rounded-full" />
        )}

        <div className="tw-text-base tw-font-bold tw-text-[#fff] mt-2">
          {status === "PENDING"
            ? "Menunggu Pembayaran"
            : status === "EXPIRED"
            ? "Transaksi Gagal"
            : "Transaksi Berhasil"}
        </div>

        {status === "COMPLETED" ? (
          <div className="tw-text-[#fff]">
            {state?.vaDatePayment != null
              ? moment(state!.vaDatePayment).format(
                  "dddd, DD MMMM YYYY | HH:mm"
                )
              : "-"}
          </div>
        ) : status === "PENDING" ? (
          <div className="tw-text-[#fff]">
            Kadaluarsa:{" "}
            {state?.vaDateExpired != null
              ? moment(state!.vaDateExpired).format(
                  "dddd, DD MMMM YYYY | HH:mm"
                )
              : "-"}
          </div>
        ) : (
          <div className="tw-text-[#fff]">------</div>
        )}

        <div className="tw-text-[#fff] tw-text-2xl tw-font-semibold mb-3 mt-2">
          {toRp(parseInt(state?.vaTrxAmount ?? 0))}{" "}
          {status === "PENDING" && (
            <span onClick={onCopyNominal}>
              <i>
                <IoCopy />
              </i>
            </span>
          )}
        </div>
      </div>
      <div className="card card-style">
        <div className="content mb-0">
          <div className="text-center mb-3">
            <div className="color-theme">Nomor Virtual Account</div>
            <div
              className="color-magenta-dark tw-text-lg tw-font-semibold tw-cursor-pointer"
              onClick={onCopy}
            >
              {state?.vaNumber ?? "-"}&nbsp;
              <i>
                <IoCopy />
              </i>
            </div>
          </div>

          <div className="tw-flex tw-justify-between mb-3">
            <div className="color-highlight tw-w-1/3">ID Transaksi</div>
            <div className="color-theme tw-w-2/3 tw-text-right">
              {state?.vaTrxId ?? "-"}
            </div>
          </div>

          <div className="tw-flex tw-justify-between mb-3">
            <div className="color-highlight tw-w-1/3">Bank</div>
            <div className="color-theme tw-w-2/3 tw-text-right">
              {state?.vaBankName ?? "-"}
            </div>
          </div>

          <div className="tw-flex tw-justify-between mb-3">
            <div className="color-highlight tw-w-1/3">Atas Nama</div>
            <div className="color-theme tw-w-2/3 tw-text-right">
              {state?.vaNAme ?? "-"}
            </div>
          </div>

          <div className="tw-flex tw-justify-between mb-3">
            <div className="color-highlight tw-w-1/3">Nominal</div>
            <div className="color-theme tw-w-2/3 tw-text-right">
              {toRp((state?.vaTrxAmount ?? 0) - 3000)}
            </div>
          </div>

          <div className="tw-flex tw-justify-between mb-3">
            <div className="color-highlight tw-w-1/3">Biaya Admin Bank</div>
            <div className="color-theme tw-w-2/3 tw-text-right">
              {toRp(3000)}
            </div>
          </div>
        </div>
      </div>

      {status === "PENDING" && (
        <div className="card card-style !tw-mb-4">
          <div className="content">
            <button
              className="btn btn-sm btn-full rounded-xs text-uppercase font-900 shadow-s bg-red-dark tw-grow tw-w-full tw-h-11"
              onClick={() => onOpenPopupCancel(state?.vaTrxId ?? "")}
            >
              Batalkan Pembayaran
            </button>
          </div>
        </div>
      )}
      <PopupConfirm
        height={200}
        show={popupCancel}
        title="Batalkan Transaksi?"
        message="Jika dibatalkan maka status transaksi menjadi kadaluarsa dan tidak bisa digunakan kembali"
        textConfirm="Ya, Batalkan"
        textCancel="Kembali"
        onConfirm={onCancel}
        onCancel={() => setPopupCancel(false)}
      />
    </>
  );
};
